import type { BadgeVariant } from '@/components/Badge';
import type { SalaryRecord, SalaryRecordStatus } from '@/mocks/db/types';
import type { SalaryAndWagesSelector } from '@/routes/_staff/salary-and-wages-mock/-utils/i18n';

/**
 * 帳票が取るデータのキー。テーブルの行の型とカラムをこれが決める。
 * `wageLedgerBook` だけは 1 行が帳票そのもの（賃金台帳 1 冊）で、
 * 中のレコードは行を開いた先の台帳詳細に出る。
 */
export type ReportGrain = 'employeePeriod' | 'wageLedgerBook' | 'employeeTaxYear' | 'officeMonth' | 'officeYear';
export type ReportCategory = 'individual' | 'organization' | 'inHouse';

/**
 * `z.enum` にそのまま渡せるよう readonly タプルで持つ。ここから型を導出することで
 * `-zod-schema.ts` 側の `as` キャストが不要になる。
 */
export const STATUTORY_REPORT_IDS = ['payslip', 'bonusPayslip', 'wageLedger', 'withholdingSlip', 'retirementWithholdingSlip', 'incomeTaxStatement', 'residentTaxStatement', 'statutoryReportSummary', 'salaryPaymentReport', 'laborInsuranceDeclaration', 'standardRemunerationNotification'] as const;
export type StatutoryReportId = (typeof STATUTORY_REPORT_IDS)[number];
export interface StatutoryReport {
  id: StatutoryReportId;
  category: ReportCategory;
  grain: ReportGrain;
  /** 集計前のレコード絞り込み。未指定なら全レコードが対象 */
  recordFilter?: (record: SalaryRecord) => boolean;
  /** officeMonth グレインの徴収税額の出典となる明細行の itemCode */
  lineItemCode?: string;
  /** officeYear グレインのグループ化に使うレコードのフィールド */
  periodBasis?: 'taxYear' | 'fiscalYear';
}
export const STATUTORY_REPORTS: StatutoryReport[] = [{
  id: 'payslip',
  category: 'individual',
  grain: 'employeePeriod',
  recordFilter: record => record.runType === 'regular'
}, {
  id: 'bonusPayslip',
  category: 'individual',
  grain: 'employeePeriod',
  recordFilter: record => record.runType === 'bonus'
},
// 賃金台帳は事業場ごとに労働者各人別で調製する帳票（労基法108条）。一覧は
// 「2026年 賃金台帳 / 山田太郎」のように本人の台帳 1 冊を 1 行として並べ、
// 中の賃金計算期間は個人詳細の賃金台帳タブで見る
{
  id: 'wageLedger',
  category: 'individual',
  grain: 'wageLedgerBook'
}, {
  id: 'withholdingSlip',
  category: 'individual',
  grain: 'employeeTaxYear'
}, {
  id: 'retirementWithholdingSlip',
  category: 'individual',
  grain: 'employeeTaxYear',
  // 退職所得は退職精算の run で支払われた分だけ。`terminationDate` で絞ると
  // 退職者の通常給与・賞与（給与所得）まで退職所得へ混入する
  recordFilter: record => record.runType === 'termination'
}, {
  id: 'incomeTaxStatement',
  category: 'organization',
  grain: 'officeMonth',
  lineItemCode: 'D-WHT'
}, {
  id: 'residentTaxStatement',
  category: 'organization',
  grain: 'officeMonth',
  lineItemCode: 'D-RES'
}, {
  id: 'statutoryReportSummary',
  category: 'organization',
  grain: 'officeYear',
  periodBasis: 'taxYear'
}, {
  id: 'salaryPaymentReport',
  category: 'organization',
  grain: 'officeYear',
  periodBasis: 'taxYear'
}, {
  id: 'laborInsuranceDeclaration',
  category: 'organization',
  grain: 'officeYear',
  periodBasis: 'fiscalYear'
}, {
  id: 'standardRemunerationNotification',
  category: 'organization',
  grain: 'officeYear',
  periodBasis: 'fiscalYear'
}];
export const DEFAULT_REPORT_ID = 'payslip' satisfies StatutoryReportId;
export function findReport(id: StatutoryReportId): StatutoryReport {
  const report = STATUTORY_REPORTS.find(entry => entry.id === id);
  return report ?? STATUTORY_REPORTS[0];
}

/**
 * 法定帳票種別 → Salary & Wages 設定タブの該当画面。
 * payroll-engine の帳票一覧などから設定マスタへ遷移するときに使う。
 */
export const STATUTORY_REPORT_SETTINGS_PATHS = {
  bonusPayslip: '/salary-and-wages-mock/settings/bonus-payslips',
  incomeTaxStatement: '/salary-and-wages-mock/settings/income-tax-statements',
  laborInsuranceDeclaration: '/salary-and-wages-mock/settings/labor-insurance-declarations',
  payslip: '/salary-and-wages-mock/settings/pay-slips',
  residentTaxStatement: '/salary-and-wages-mock/settings/resident-tax-statements',
  retirementWithholdingSlip: '/salary-and-wages-mock/settings/retirement-withholding-slips',
  salaryPaymentReport: '/salary-and-wages-mock/settings/salary-payment-reports',
  standardRemunerationNotification: '/salary-and-wages-mock/settings/standard-remuneration-notifications',
  statutoryReportSummary: '/salary-and-wages-mock/settings/statutory-report-summaries',
  wageLedger: '/salary-and-wages-mock/settings/wage-ledgers',
  withholdingSlip: '/salary-and-wages-mock/settings/withholding-slips'
} as const satisfies Record<StatutoryReportId, `/salary-and-wages-mock/settings/${string}`>;

// ---------------------------------------------------------------------------
// In-house reports (社内帳票)
// ---------------------------------------------------------------------------

export const IN_HOUSE_REPORT_IDS = ['jobGradePayRange', 'promotionSummary', 'fairnessCheck'] as const;
export type InHouseReportId = (typeof IN_HOUSE_REPORT_IDS)[number];

// ---------------------------------------------------------------------------
// Analysis items (分析)
// ---------------------------------------------------------------------------

export const ANALYSIS_ITEM_IDS = ['analysisList', 'analysisAgeComparison', 'analysisRaiseImpact', 'analysisLeavePayForecast'] as const;
export type AnalysisItemId = (typeof ANALYSIS_ITEM_IDS)[number];

// ---------------------------------------------------------------------------
// Combined menu ID — URL search param accepts any of the three groups
// ---------------------------------------------------------------------------

export type ReportMenuId = StatutoryReportId | InHouseReportId | AnalysisItemId;
export function isStatutoryReportId(id: ReportMenuId): id is StatutoryReportId {
  return (STATUTORY_REPORT_IDS as readonly string[]).includes(id);
}
export function isInHouseReportId(id: ReportMenuId): id is InHouseReportId {
  return (IN_HOUSE_REPORT_IDS as readonly string[]).includes(id);
}
export function isAnalysisItemId(id: ReportMenuId): id is AnalysisItemId {
  return (ANALYSIS_ITEM_IDS as readonly string[]).includes(id);
}

// ---------------------------------------------------------------------------
// Sidebar structure
// ---------------------------------------------------------------------------

export interface SidebarSection {
  category: ReportCategory;
  reportIds: ReportMenuId[];
}
export const SIDEBAR_SECTIONS: SidebarSection[] = [{
  category: 'individual',
  reportIds: ['payslip', 'bonusPayslip', 'wageLedger', 'withholdingSlip', 'retirementWithholdingSlip']
}, {
  category: 'organization',
  reportIds: ['incomeTaxStatement', 'residentTaxStatement', 'statutoryReportSummary', 'salaryPaymentReport', 'laborInsuranceDeclaration', 'standardRemunerationNotification']
}, {
  category: 'inHouse',
  reportIds: ['jobGradePayRange', 'promotionSummary', 'fairnessCheck']
}];
export const ANALYSIS_LIST_IDS: AnalysisItemId[] = ['analysisList'];
export const ANALYSIS_RECENT_IDS: AnalysisItemId[] = ['analysisAgeComparison', 'analysisRaiseImpact', 'analysisLeavePayForecast'];

// ---------------------------------------------------------------------------
// i18n selectors
// ---------------------------------------------------------------------------

export const statutoryReportSelectors = {
  bonusPayslip: $ => $.reports.names.bonusPayslip,
  incomeTaxStatement: $ => $.reports.names.incomeTaxStatement,
  laborInsuranceDeclaration: $ => $.reports.names.laborInsuranceDeclaration,
  payslip: $ => $.reports.names.payslip,
  residentTaxStatement: $ => $.reports.names.residentTaxStatement,
  retirementWithholdingSlip: $ => $.reports.names.retirementWithholdingSlip,
  salaryPaymentReport: $ => $.reports.names.salaryPaymentReport,
  standardRemunerationNotification: $ => $.reports.names.standardRemunerationNotification,
  statutoryReportSummary: $ => $.reports.names.statutoryReportSummary,
  wageLedger: $ => $.reports.names.wageLedger,
  withholdingSlip: $ => $.reports.names.withholdingSlip
} satisfies Record<StatutoryReportId, SalaryAndWagesSelector>;
export const inHouseReportSelectors = {
  fairnessCheck: $ => $.reports.names.fairnessCheck,
  jobGradePayRange: $ => $.reports.names.jobGradePayRange,
  promotionSummary: $ => $.reports.names.promotionSummary
} satisfies Record<InHouseReportId, SalaryAndWagesSelector>;
export const analysisItemSelectors = {
  analysisAgeComparison: $ => $.analysis.recent.ageComparison,
  analysisLeavePayForecast: $ => $.analysis.recent.leavePayForecast,
  analysisList: $ => $.analysis.list,
  analysisRaiseImpact: $ => $.analysis.recent.raiseImpact
} satisfies Record<AnalysisItemId, SalaryAndWagesSelector>;
export const reportMenuItemSelectors: Record<ReportMenuId, SalaryAndWagesSelector> = {
  ...statutoryReportSelectors,
  ...inHouseReportSelectors,
  ...analysisItemSelectors
};
export const reportCategorySelectors = {
  inHouse: $ => $.reports.categories.inHouse,
  individual: $ => $.reports.categories.individual,
  organization: $ => $.reports.categories.organization
} satisfies Record<ReportCategory, SalaryAndWagesSelector>;

/** SalaryRecordStatus → バッジ配色。 */
export const statusBadgeVariant: Record<SalaryRecordStatus, BadgeVariant> = {
  approving: 'mutedWarning',
  calculated: 'secondaryMuted',
  calculating: 'mutedWarning',
  cancelled: 'errorMuted',
  delivered: 'complementaryMuted',
  draft: 'neutralMuted',
  finalized: 'primaryMuted',
  paid: 'mutedSuccess'
};